import { useCallback, useMemo, useRef, useState } from "react";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import {
	aggridHeaderClass,
	commonNumberCellStyle,
} from "src/components/common/common";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { formatNumber } from "src/utils/number/numberUtils";
import { formatDate } from "src/utils/string/stringUtil";

import {
	defaultTQC3010_R01HeaderDto,
	TQC3010_R01HeaderDto,
} from "./Dto_TQC3010_R01";
import { useTQC3010_R01Store } from "./Store_TQC3010_R01";

const COLUMN_STATE_KEY = "TQC3010_R01-header-column-state";

interface Props {
	onHeaderRowClick: (row: TQC3010_R01HeaderDto) => void;
}

export const HeaderTable_TQC3010_R01 = ({ onHeaderRowClick }: Props) => {
	const {
		TQC3010_R01List,
		setTQC3010_R01List,
		selectedTQC3010_R01,
		initSelectedTQC3010_R01,
		setColumnState,
		scrollTop,
		setScrollTop,
	} = useTQC3010_R01Store();
	const gridRef = useRef<AgGridReact<TQC3010_R01HeaderDto>>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const [, setHoveredRowIndex] = useState<number | null>(null);

	const { getRowId, createRowClassRules } =
		useGridSettingContent<TQC3010_R01HeaderDto>({
			gridRef,
			key: "valueIndex",
			gridData: TQC3010_R01List,
			defaultGridData: defaultTQC3010_R01HeaderDto,
			selectedRow: selectedTQC3010_R01,
			originalGridData: TQC3010_R01List,
			updateRow: () => undefined,
		});

	const columnDefs: ColDef<TQC3010_R01HeaderDto>[] = useMemo(
		() => [
			...getDefaultRowIndexColumnConfig(),
			{ headerName: "사업장", field: "compNm", width: 110, headerClass: aggridHeaderClass },
			{
				headerName: "날짜",
				field: "transportDt",
				width: 110,
				headerClass: aggridHeaderClass,
				valueFormatter: ({ value }) => (value ? formatDate(new Date(value)) : ""),
			},
			{ headerName: "거래처", field: "customCd", width: 90, headerClass: aggridHeaderClass },
			{ headerName: "상호", field: "customNm", width: 120, headerClass: aggridHeaderClass },
			{ headerName: "수불유형", field: "inoutClassNm", width: 110, headerClass: aggridHeaderClass },
			{ headerName: "품목코드", field: "itemCd", width: 120, headerClass: aggridHeaderClass },
			{ headerName: "품명", field: "itemNm", width: 140, headerClass: aggridHeaderClass },
			{ headerName: "규격", field: "itemSpec", width: 110, headerClass: aggridHeaderClass },
			{
				headerName: "출고량",
				field: "qty",
				width: 90,
				headerClass: aggridHeaderClass,
				cellStyle: commonNumberCellStyle,
				valueFormatter: ({ value }) => (value == null ? "" : formatNumber(value)),
			},
			{ headerName: "단위", field: "unit", width: 70, headerClass: aggridHeaderClass },
			{ headerName: "LotNo", field: "lotNo", width: 130, headerClass: aggridHeaderClass },
			{
				headerName: "단가",
				field: "cost",
				width: 90,
				headerClass: aggridHeaderClass,
				cellStyle: commonNumberCellStyle,
				valueFormatter: ({ value }) => (value == null ? "" : formatNumber(value)),
			},
			{
				headerName: "검사여부",
				colId: "inspectStatus",
				width: 90,
				headerClass: aggridHeaderClass,
				// 합계 행에는 검사여부가 없다. 빈 데이터가 "미검사"로 보이지 않게 막는다.
				valueGetter: ({ data, node }) =>
					node?.rowPinned ? "" : data?.inspectNo ? "검사완료" : "미검사",
			},
		],
		[],
	);

	/** 합계 행. 출고량만 더한다. (단가는 건별 단가라 합산이 의미 없다) */
	const pinnedBottomRow = useMemo(() => {
		const totalQty = TQC3010_R01List.reduce(
			(sum, row) => sum + (Number(row.qty) || 0),
			0,
		);
		return [
			{
				valueIndex: "",
				rowIndex: "",
				compNm: `${TQC3010_R01List.length.toLocaleString()}건`,
				qty: totalQty,
				editable: false,
			},
		] as unknown as TQC3010_R01HeaderDto[];
	}, [TQC3010_R01List]);

	const rowClassRules = useMemo(
		() =>
			createRowClassRules<TQC3010_R01HeaderDto>({
				idKey: "valueIndex",
				selectedList: selectedTQC3010_R01?.valueIndex
					? [selectedTQC3010_R01.valueIndex]
					: [],
				deletedList: [],
			}),
		[createRowClassRules, selectedTQC3010_R01],
	);

	const handleRowClick = useCallback(
		(event: RowClickedEvent<TQC3010_R01HeaderDto>) => {
			// 합계 행은 선택 대상이 아니다. 클릭하면 품목코드 없이 검사항목을 조회하게 된다.
			if (event.node.rowPinned) return;
			if (event.data) onHeaderRowClick(event.data);
		},
		[onHeaderRowClick],
	);

	return (
		<GridContextWrapper<TQC3010_R01HeaderDto>
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={TQC3010_R01List}
			setPaddedGridData={setTQC3010_R01List}
			initSelected={initSelectedTQC3010_R01}
			defaultRowData={defaultTQC3010_R01HeaderDto}
			allSelected={() => undefined}
			baseColumnDefs={columnDefs}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
		>
			<Grid_Select<TQC3010_R01HeaderDto>
				ref={gridRef}
				rowData={TQC3010_R01List}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={rowClassRules}
				onRowClicked={handleRowClick}
				pinnedBottomRowData={pinnedBottomRow}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				setHoveredRowIndex={setHoveredRowIndex}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
